import queryString from "query-string";
import {
  OrderByDirection,
  ProgrammingStructureFilterFieldsInput,
  ProgrammingStructureOrderByFields,
} from "@models/ISchema";
import {
  BooleanFilterPair,
  DEFAULT_SORT,
  EMPTY_FILTERS,
  PrimaryActionLabel,
  ProgrammingStructuresFilters,
  ProgrammingStructuresSearchParams,
  ProgrammingStructuresSort,
  STRUCTURE_SOURCE,
  STRUCTURE_SOURCE_ALIASES,
  StructureSourceCode,
} from "@services/types/programming-structures/programmingStructuresFiltersTypes";

const SOURCE_LABEL_KEYS: Record<StructureSourceCode, string> = {
  [STRUCTURE_SOURCE.base]: "programming-structures.table.body.source.base",
  [STRUCTURE_SOURCE.user]: "programming-structures.table.body.source.user-defined",
};

const STRUCTURE_SOURCE_CODES = Object.values(STRUCTURE_SOURCE);

/**
 * Every accepted spelling, lowercased, mapped back to its canonical code.
 * Matching is case-insensitive because the backend column collation is
 * `utf8mb4_unicode_ci`, so the database already treats the spellings as equal.
 */
const CANONICAL_SOURCE_BY_SPELLING = new Map<string, StructureSourceCode>(
  STRUCTURE_SOURCE_CODES.flatMap(code => [
    [code.toLowerCase(), code] as const,
    ...STRUCTURE_SOURCE_ALIASES[code].map(alias => [alias.toLowerCase(), code] as const),
  ]),
);

/** Maps any accepted spelling to its canonical code, or `null` if unknown. */
export const resolveStructureSourceCode = (source?: string | null): StructureSourceCode | null =>
  CANONICAL_SOURCE_BY_SPELLING.get(source?.trim().toLowerCase() ?? "") ?? null;

/**
 * Resolve the i18n key for a structure source. Returns `null` for codes that
 * are not mapped, so callers can fall back to the raw value instead of
 * rendering an empty pill — source codes live in a backend lookup table and a
 * new row would otherwise disappear silently.
 */
export const getStructureSourceLabelKey = (source?: string | null): string | null => {
  const code = resolveStructureSourceCode(source);

  return code ? SOURCE_LABEL_KEYS[code] : null;
};

const getCheckedSources = (sources: ProgrammingStructuresFilters["sources"]) =>
  STRUCTURE_SOURCE_CODES.filter(code => sources[code]);

/**
 * Options inside a filter group are mutually exclusive: checking one clears its
 * siblings, and checking the active one clears the group. Unlike a radio group
 * this keeps "nothing selected" — which means "unconstrained" — reachable
 * without going through `Limpiar filtros`.
 */
export const toggleExclusiveSource = (
  sources: ProgrammingStructuresFilters["sources"],
  code: StructureSourceCode,
): ProgrammingStructuresFilters["sources"] => {
  const shouldCheck = !sources[code];

  return STRUCTURE_SOURCE_CODES.reduce(
    (next, current) => ({ ...next, [current]: shouldCheck && current === code }),
    {} as ProgrammingStructuresFilters["sources"],
  );
};

/** Mutually exclusive counterpart for the yes/no groups. */
export const toggleExclusivePair = (
  pair: BooleanFilterPair,
  key: keyof BooleanFilterPair,
): BooleanFilterPair => ({
  yes: key === "yes" && !pair.yes,
  no: key === "no" && !pair.no,
});

/**
 * A pair of checkboxes only constrains the query when exactly one is checked.
 * Both checked and both unchecked mean "any value", which `BooleanFilterInput`
 * can only express by omitting the field.
 */
const resolveBooleanPair = ({ yes, no }: BooleanFilterPair): boolean | null => {
  if (yes === no) return null;

  return yes;
};

export const buildFilterFields = (
  filters: ProgrammingStructuresFilters,
): ProgrammingStructureFilterFieldsInput => {
  const fields: ProgrammingStructureFilterFieldsInput = {};
  const structureCode = filters?.structureCode?.trim() ?? "";
  // Only canonical codes are sent: aliases are an inbound/display concern.
  const checkedSources = getCheckedSources(filters?.sources ?? EMPTY_FILTERS.sources);
  const isPublished = resolveBooleanPair(filters?.isPublished ?? EMPTY_FILTERS.isPublished);
  const isVisible = resolveBooleanPair(filters?.isVisible ?? EMPTY_FILTERS.isVisible);

  if (filters?.subject?.code) fields.subjectCodes = { in: [filters.subject.code] };

  if (structureCode) fields.code = { contains: structureCode };

  if (checkedSources.length) fields.source = { in: checkedSources };

  if (isPublished !== null) fields.isPublished = { is: isPublished };

  if (isVisible !== null) fields.isVisible = { is: isVisible };

  return fields;
};

/**
 * Stable representation used to compare the edited filters against the applied
 * ones. An array keeps the ordering explicit, so key insertion order can never
 * produce a false "pending changes" positive. The subject name is excluded on
 * purpose: it is a display label, not part of the query.
 */
export const serializeFilters = (filters: ProgrammingStructuresFilters): string =>
  JSON.stringify([
    filters?.subject?.code ?? "",
    filters?.structureCode?.trim() ?? "",
    getCheckedSources(filters?.sources ?? EMPTY_FILTERS.sources),
    filters?.isPublished?.yes ?? false,
    filters?.isPublished?.no ?? false,
    filters?.isVisible?.yes ?? false,
    filters?.isVisible?.no ?? false,
  ]);

export const hasAnySelection = (filters: ProgrammingStructuresFilters): boolean =>
  serializeFilters(filters) !== serializeFilters(EMPTY_FILTERS);

/** True when any control inside the "Más filtros" panel holds a value. */
export const hasAdvancedSelection = (filters: ProgrammingStructuresFilters): boolean =>
  getCheckedSources(filters?.sources ?? EMPTY_FILTERS.sources).length > 0 ||
  Boolean(filters?.isPublished?.yes) ||
  Boolean(filters?.isPublished?.no) ||
  Boolean(filters?.isVisible?.yes) ||
  Boolean(filters?.isVisible?.no);

/**
 * The primary action button label. `update` wins whenever the rows on screen
 * came from a non-empty applied filter set, because they are stale regardless
 * of which field the user touched.
 */
export const resolvePrimaryActionLabel = ({
  form,
  applied,
  isAdvancedPanelOpen,
}: {
  form: ProgrammingStructuresFilters;
  applied: ProgrammingStructuresFilters;
  isAdvancedPanelOpen: boolean;
}): PrimaryActionLabel => {
  const isPending = serializeFilters(form) !== serializeFilters(applied);

  if (isPending && hasAnySelection(applied)) return "update";

  if (isAdvancedPanelOpen || hasAdvancedSelection(form)) return "apply";

  return "search";
};

const parseBooleanPair = (value?: string | null): BooleanFilterPair => {
  if (value === "true") return { yes: true, no: false };

  if (value === "false") return { yes: false, no: true };

  return { yes: false, no: false };
};

const firstValue = (value: string | (string | null)[] | null): string | null => {
  if (Array.isArray(value)) return value[0] ?? null;

  return value ?? null;
};

/**
 * Reads an inbound deep link into filter state. The query string is only ever
 * consumed: the page strips it right after hydrating, so there is no matching
 * serializer.
 */
export const parseFiltersFromUrl = (search: string): ProgrammingStructuresSearchParams => {
  const params = queryString.parse(search ?? "");

  const subjectCode = firstValue(params.subjectCode);
  const subjectName = firstValue(params.subjectName);
  const sortField = firstValue(params.sortField);
  const sortDirection = firstValue(params.sortDirection);
  const page = Number(firstValue(params.page));

  // Groups are mutually exclusive, so a deep link listing several codes keeps
  // only the first recognised one.
  const selectedSource =
    (firstValue(params.source) ?? "")
      .split(",")
      .map(resolveStructureSourceCode)
      .find((code): code is StructureSourceCode => code !== null) ?? null;

  const isValidSortField = Object.values(ProgrammingStructureOrderByFields).includes(
    sortField as ProgrammingStructureOrderByFields,
  );
  const isValidSortDirection = Object.values(OrderByDirection).includes(
    sortDirection as OrderByDirection,
  );

  const sort: ProgrammingStructuresSort = isValidSortField
    ? {
        field: sortField as ProgrammingStructureOrderByFields,
        direction: isValidSortDirection
          ? (sortDirection as OrderByDirection)
          : DEFAULT_SORT.direction,
      }
    : DEFAULT_SORT;

  return {
    filters: {
      subject: subjectCode ? { code: subjectCode, name: subjectName ?? "" } : null,
      structureCode: firstValue(params.structureCode) ?? "",
      sources: {
        [STRUCTURE_SOURCE.base]: selectedSource === STRUCTURE_SOURCE.base,
        [STRUCTURE_SOURCE.user]: selectedSource === STRUCTURE_SOURCE.user,
      },
      isPublished: parseBooleanPair(firstValue(params.isPublished)),
      isVisible: parseBooleanPair(firstValue(params.isVisible)),
    },
    sort,
    page: Number.isInteger(page) && page > 0 ? page : 1,
  };
};
